// Desktop capture spool panel, web half (UX spec 7.9 and 9.9, FE-17). Desktop shell only. It states
// exactly what local capture would contain, says plainly when the server hasn't enabled it, and
// shows the spool's own coverage label verbatim. No upload or delete actions are offered here yet:
// those spool controls are not wired to this panel.
import { useSyncExternalStore } from 'react';
import type { DesktopCaptureState } from '@bcio-voice/call-client/core';
import { useWorkspace } from '../../state/workspace.tsx';
import { useDesktop } from '../../desktop/bridge.ts';
import { capability } from '../../lib/capabilities.ts';
import { Diagnostics } from '../../ui/Diagnostics.tsx';
import type { LocalCaptureView } from '../../call/desktopCapture.ts';

export const CAPTURE_SCOPE = 'This computer: your microphone and the call audio you hear. It is never the official recording; the server recording stays the record of the call.';
export const CAPTURE_UNAVAILABLE = 'Local capture is unavailable until the server enables it. Your calls and the official recording aren’t affected.';

const LOADING: LocalCaptureView = { status: 'loading', producer: null };
const noopSubscribe = () => () => undefined;
const loading = () => LOADING;

const REASONS: Record<string, string> = {
  no_policy: 'Waiting for the call’s recording state from the server.',
  capability_not_enabled: 'The server hasn’t enabled local capture.',
  capability_not_allowed: 'Your role doesn’t include local capture.',
  consent_not_verified: 'Not everyone on the call has agreed to recording.',
  recording_not_active: 'The call isn’t being recorded.',
  recording_paused: 'The call recording is paused.',
  call_not_active: 'The call isn’t connected.',
  call_ended: 'The call ended.',
  feature_disabled: 'Local capture is turned off in this desktop app.',
  not_signed_in: 'Sign in to the desktop app to capture locally.',
  disk_low: 'This computer is low on disk space.',
  capture_disk_low: 'This computer is low on disk space.',
  capture_quota_full: 'The local recording space is full.',
};

/** Plain words for the producer's phase (7.9). Exported for tests. */
export function phaseText(state: DesktopCaptureState): string {
  switch (state.phase) {
    case 'idle':
      return 'Not capturing. Capture runs only while a call is recorded with everyone’s consent.';
    case 'starting':
      return 'Starting local capture…';
    case 'capturing':
      return 'Capturing this call on this computer.';
    case 'paused':
      return 'Paused with the call recording.';
    case 'stopping':
      return 'Finishing the local copy…';
    case 'stopped':
      return 'Stopped.';
    case 'denied':
      return 'Not capturing.';
    case 'unavailable':
      return 'Local capture isn’t available on this computer.';
    default:
      return 'Local capture failed.';
  }
}

export function SpoolPanel() {
  const ws = useWorkspace();
  const desktop = useDesktop();
  const store = ws.localCapture ?? null;
  const view = useSyncExternalStore(store?.subscribe ?? noopSubscribe, store?.getSnapshot ?? loading, store?.getSnapshot ?? loading);
  if (!desktop) return null;
  const cap = capability(ws.config.value, 'desktop.capture');
  const enabled = cap?.status === 'enabled' && cap.allowed;
  const producer = view.producer;
  return (
    <section className="settings-tile settings-wide spool-panel" aria-labelledby="set-spool">
      <h2 id="set-spool" className="section-title">Local copy of calls</h2>
      <p className="field-hint">{CAPTURE_SCOPE}</p>
      {!cap ? (
        <p className="field-hint" role="status">Checking whether local capture is available…</p>
      ) : !enabled ? (
        <>
          <p className="inline-reason">{cap.allowed ? CAPTURE_UNAVAILABLE : 'Your role doesn’t include local capture.'}</p>
          <Diagnostics diagnostic={cap.reason_code || cap.detail ? { code: cap.reason_code, detail: cap.detail } : null} />
        </>
      ) : !store ? (
        <p className="inline-reason">This version of the desktop app can’t capture locally.</p>
      ) : view.status === 'error' ? (
        <p className="inline-reason" role="status">Local capture couldn’t start on this computer.</p>
      ) : !producer ? (
        <p className="field-hint" role="status">Getting local capture ready…</p>
      ) : (
        <div role="status">
          <p className="spool-phase">{phaseText(producer)}{producer.reason && REASONS[producer.reason] ? ` ${REASONS[producer.reason]}` : ''}</p>
          {producer.coverage_label ? <p className="spool-label">{producer.coverage_label}</p> : null}
          {producer.truncated ? <p className="field-hint">This copy ends before the call did.</p> : null}
          <Diagnostics diagnostic={producer.reason ? { code: producer.reason, detail: null } : null} />
        </div>
      )}
    </section>
  );
}
